import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import "./index.scss";

export const ACCOUNT_DELETION_BILLING_IMPACTS = Object.freeze({
    NO_ACTIVE_SUBSCRIPTION: "NO_ACTIVE_SUBSCRIPTION",
    ACTIVE_SCHEDULE_UNCHANGED_DURING_RECOVERY: "ACTIVE_SCHEDULE_UNCHANGED_DURING_RECOVERY",
    EXISTING_CANCELLATION_UNCHANGED: "EXISTING_CANCELLATION_UNCHANGED",
    UNKNOWN: "UNKNOWN",
});

const KNOWN_OWNER_IMPACTS = new Set([
    ACCOUNT_DELETION_BILLING_IMPACTS.NO_ACTIVE_SUBSCRIPTION,
    ACCOUNT_DELETION_BILLING_IMPACTS.ACTIVE_SCHEDULE_UNCHANGED_DURING_RECOVERY,
    ACCOUNT_DELETION_BILLING_IMPACTS.EXISTING_CANCELLATION_UNCHANGED,
]);

export const normalizeAccountDeletionBillingImpact = (value) => (
    typeof value === "string" && KNOWN_OWNER_IMPACTS.has(value.trim().toUpperCase())
        ? value.trim().toUpperCase()
        : ACCOUNT_DELETION_BILLING_IMPACTS.UNKNOWN
);

const validRecoveryWindowDays = (value) => (
    Number.isSafeInteger(value) && value > 0 && value <= 3650
);

const errorDetailParts = (error) => [
    typeof error?.code === "string" && error.code.trim()
        ? { key: "accountDeletionBillingImpact.errorCode", values: { code: error.code.trim() } }
        : null,
    typeof error?.requestId === "string" && error.requestId.trim()
        ? { key: "accountDeletionBillingImpact.requestId", values: { requestId: error.requestId.trim() } }
        : null,
].filter(Boolean);

export default function AccountDeletionBillingImpactNotice({
    recoveryWindowDays,
    recoveryDeadline = null,
    impact = ACCOUNT_DELETION_BILLING_IMPACTS.UNKNOWN,
    nonEnumerating = false,
    isLoading = false,
    error = null,
}) {
    const { t } = useTranslation();
    const hasRecoveryWindowDays = validRecoveryWindowDays(recoveryWindowDays);
    const normalizedImpact = normalizeAccountDeletionBillingImpact(impact);
    const shouldShowFallback = nonEnumerating
        || normalizedImpact === ACCOUNT_DELETION_BILLING_IMPACTS.UNKNOWN;
    const shouldShowActive = !nonEnumerating
        && normalizedImpact === ACCOUNT_DELETION_BILLING_IMPACTS.ACTIVE_SCHEDULE_UNCHANGED_DURING_RECOVERY;
    const shouldShowScheduledCancellation = !nonEnumerating
        && normalizedImpact === ACCOUNT_DELETION_BILLING_IMPACTS.EXISTING_CANCELLATION_UNCHANGED;
    const detailParts = errorDetailParts(error);

    return (
        <section
            className="account-deletion-billing-impact"
            aria-label={t("accountDeletionBillingImpact.sectionLabel")}
        >
            <p className="account-deletion-billing-impact__recovery">
                {hasRecoveryWindowDays
                    ? t("accountDeletionBillingImpact.recovery.withDays", { recoveryWindowDays })
                    : t("accountDeletionBillingImpact.recovery.generic")}
            </p>
            {recoveryDeadline ? (
                <p className="account-deletion-billing-impact__deadline">
                    {t("accountDeletionBillingImpact.recovery.deadline", { deadline: recoveryDeadline })}
                </p>
            ) : null}
            {isLoading ? (
                <p className="account-deletion-billing-impact__status" role="status">
                    {t("accountDeletionBillingImpact.loading")}
                </p>
            ) : null}
            {shouldShowActive ? (
                <p className="account-deletion-billing-impact__warning" role="alert">
                    {hasRecoveryWindowDays
                        ? t("accountDeletionBillingImpact.activeSubscription.withDays", { recoveryWindowDays })
                        : t("accountDeletionBillingImpact.activeSubscription.generic")}
                </p>
            ) : null}
            {shouldShowScheduledCancellation ? (
                <p className="account-deletion-billing-impact__warning">
                    {hasRecoveryWindowDays
                        ? t("accountDeletionBillingImpact.existingCancellation.withDays", { recoveryWindowDays })
                        : t("accountDeletionBillingImpact.existingCancellation.generic")}
                </p>
            ) : null}
            {shouldShowFallback ? (
                <p className="account-deletion-billing-impact__warning" role="alert">
                    {hasRecoveryWindowDays
                        ? t("accountDeletionBillingImpact.fallback.withDays", { recoveryWindowDays })
                        : t("accountDeletionBillingImpact.fallback.generic")}
                </p>
            ) : null}
            {error ? (
                <p className="account-deletion-billing-impact__status">
                    {t("accountDeletionBillingImpact.summaryUnavailable")}
                    {detailParts.length > 0 ? (
                        <>
                            {" "}
                            {detailParts.map((part) => t(part.key, part.values)).join(" ")}
                        </>
                    ) : null}
                </p>
            ) : null}
        </section>
    );
}

AccountDeletionBillingImpactNotice.propTypes = {
    recoveryWindowDays: PropTypes.number,
    recoveryDeadline: PropTypes.string,
    impact: PropTypes.oneOf(Object.values(ACCOUNT_DELETION_BILLING_IMPACTS)),
    nonEnumerating: PropTypes.bool,
    isLoading: PropTypes.bool,
    error: PropTypes.shape({
        code: PropTypes.string,
        requestId: PropTypes.string,
    }),
};
